import React, { useState, useEffect } from 'react';
import { List, Eye, Search, RefreshCw, Package, Calendar, User, FileText } from 'lucide-react';
import { dataInspectionService } from '../../../../../services/dataInspectionService';
import { PListDto, PListLineDto } from '../../../../../types/picking';
import { BackButton } from '../../../../common/BackButton';

interface PListViewerProps {
    onBack: () => void;
}

export const PListViewer: React.FC<PListViewerProps> = ({ onBack }) => {
    const [pLists, setPLists] = useState<PListDto[]>([]);
    const [selectedPList, setSelectedPList] = useState<PListDto | null>(null);
    const [pListLines, setPListLines] = useState<PListLineDto[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>('');
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        loadPLists();
    }, []);

    const loadPLists = async () => {
        try {
            setLoading(true);
            setError('');
            const data = await dataInspectionService.getAllPLists();
            setPLists(data);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'En feil oppstod');
        } finally {
            setLoading(false);
        }
    };

    const loadPListLines = async (pListId: number) => {
        try {
            setLoading(true);
            setError('');
            const data = await dataInspectionService.getPListWithLines(pListId);
            setSelectedPList(data.header);
            setPListLines(data.lines);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'En feil oppstod');
        } finally {
            setLoading(false);
        }
    };

    const handleViewPList = (pList: PListDto) => {
        loadPListLines(pList.id);
    };

    const handleBackToList = () => {
        setSelectedPList(null);
        setPListLines([]);
    };

    const filteredPLists = pLists.filter(pList => 
        pList.pListID.toString().includes(searchTerm) ||
        pList.lockedBy?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pList.description1?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const getStatusColor = (status: number) => {
        switch (status) {
            case 10: return 'bg-blue-100 text-blue-800';
            case 20: return 'bg-yellow-100 text-yellow-800';
            case 30: return 'bg-orange-100 text-orange-800';
            case 40: return 'bg-green-100 text-green-800';
            case 45: return 'bg-purple-100 text-purple-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    const getStatusText = (status: number) => {
        switch (status) {
            case 10: return 'Godkjent';
            case 20: return 'Under arbeid';
            case 30: return 'Behandlet ikke komplett';
            case 40: return 'Behandlet komplett';
            case 45: return 'Til pakking';
            default: return `Status ${status}`;
        }
    };

    if (selectedPList) {
        return (
            <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center gap-3 mb-6">
                    <BackButton
                        onClick={handleBackToList}
                        className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg"
                        iconSize={16}
                        label="Tilbake til liste"
                    />
                    <div className="flex items-center gap-2">
                        <List className="w-5 h-5 text-blue-600" />
                        <h1 className="text-xl font-semibold">Plukkliste {selectedPList.pListID}</h1>
                    </div>
                </div>

                {/* PList Header Info */}
                <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div>
                            <label className="text-sm font-medium text-gray-500">Plukkliste Nr.</label>
                            <div className="text-lg font-semibold text-gray-900">{selectedPList.pListID}</div>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-500">Status</label>
                            <div className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(selectedPList.pListStatus)}`}>
                                {getStatusText(selectedPList.pListStatus)}
                            </div>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-500">Operatør</label>
                            <div className="text-gray-900">{selectedPList.lockedBy || 'Ikke tildelt'}</div>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-500">Beskrivelse</label>
                            <div className="text-gray-900">{selectedPList.description1 || 'N/A'}</div>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-500">Lager</label>
                            <div className="text-gray-900">{pListLines.length > 0 ? pListLines[0].warehouseID : 'N/A'}</div>
                        </div>
                        <div>
                            <label className="text-sm font-medium text-gray-500">Opprettet</label>
                            <div className="text-gray-900">
                                {selectedPList.pListDate ? new Date(selectedPList.pListDate).toLocaleDateString('nb-NO') : 'N/A'}
                            </div>
                        </div>
                    </div>
                </div>

                {/* PList Lines */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                    <div className="p-6 border-b border-gray-200">
                        <h2 className="text-lg font-semibold text-gray-900">Plukklinjer ({pListLines.length})</h2>
                    </div>
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Linje</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ordre</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vare</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Mengde</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plukket</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Gjenstår</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Lokasjon</th>
                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {pListLines.map(line => (
                                    <tr key={line.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                            {line.pListLineNo}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {line.orderNo}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {line.itemNo}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {line.qty}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {line.qtyPicked || 0}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {line.remainingQty || 0}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                            {line.binCode}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(line.lineStatus)}`}>
                                                {getStatusText(line.lineStatus)}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center gap-3 mb-6">
                <BackButton
                    onClick={onBack}
                    className="px-4 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg"
                    iconSize={16}
                    label="Tilbake til grunndata"
                />
                <div className="flex items-center gap-2">
                    <List className="w-5 h-5 text-blue-600" />
                    <h1 className="text-xl font-semibold">Plukklister</h1>
                </div>
            </div>

            {/* Search and Controls */}
            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-200">
                <div className="flex flex-col sm:flex-row gap-4 items-center justify-between">
                    <div className="flex items-center gap-4 flex-1">
                        <div className="relative flex-1 max-w-md">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                            <input
                                type="text"
                                placeholder="Søk etter plukkliste nr., operatør eller beskrivelse..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                        </div>
                    </div>
                    <button
                        onClick={loadPLists}
                        disabled={loading}
                        className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                    >
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                        Oppdater
                    </button>
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-red-700">
                        <div className="w-2 h-2 bg-red-400 rounded-full"></div>
                        <span className="font-medium">Feil:</span>
                    </div>
                    <p className="text-red-600 mt-1">{error}</p>
                </div>
            )}

            {/* PList Table */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="p-6 border-b border-gray-200">
                    <h2 className="text-lg font-semibold text-gray-900">Plukklister ({filteredPLists.length})</h2>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Plukkliste Nr.</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Operatør</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Beskrivelse</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Prioritet</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dato</th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Handlinger</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {filteredPLists.map(pList => (
                                <tr key={pList.id} className="hover:bg-gray-50">
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                        {pList.pListID}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(pList.pListStatus)}`}>
                                            {getStatusText(pList.pListStatus)}
                                        </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {pList.lockedBy || 'Ikke tildelt'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {pList.description1 || 'N/A'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {pList.priority}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                        {pList.pListDate ? new Date(pList.pListDate).toLocaleDateString('nb-NO') : 'N/A'}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                        <button
                                            onClick={() => handleViewPList(pList)}
                                            className="flex items-center gap-1 text-blue-600 hover:text-blue-900 hover:bg-blue-50 px-2 py-1 rounded transition-colors"
                                        >
                                            <Eye className="w-4 h-4" />
                                            Vis detaljer
                                        </button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};